/** Derived values. One canonical count feeds the headline, badges, KPI and queue tabs. */

import type { Agent, AgentId, Proposal, Snapshot, TwinRequest } from '../api/types.js';

/**
 * Queue order: customer replies first, then other T3 content, then T4 under "Karar senin". Within a rank
 * cards of the same type sit together (audit 25 Oct 2026, F4), in the order their oldest card came in,
 * so the grouped list, J/K and "next" after a decision walk the same order.
 */
export function orderedProposals(snapshot: Snapshot): Proposal[] {
  const rank = (proposal: Proposal) => (proposal.tier !== 'T3' ? 2 : proposal.kind === 'customer_reply' ? 0 : 1);
  const byAge = [...snapshot.proposals].sort((a, b) => rank(a) - rank(b) || a.queuedAt - b.queuedAt);
  const firstSeen = new Map<string, number>();
  byAge.forEach((proposal, index) => {
    const key = `${rank(proposal)}:${proposal.kindLabel}`;
    if (!firstSeen.has(key)) firstSeen.set(key, index);
  });
  const groupOf = (proposal: Proposal) => firstSeen.get(`${rank(proposal)}:${proposal.kindLabel}`) ?? 0;
  return byAge
    .map((proposal, index) => ({ proposal, index }))
    .sort((a, b) => groupOf(a.proposal) - groupOf(b.proposal) || a.index - b.index)
    .map((item) => item.proposal);
}

/** The queue as type groups with their counts ("İlan başlığı · 4"), in queue order. */
export interface ProposalGroup {
  label: string;
  tier: Proposal['tier'];
  items: Proposal[];
}

export function proposalGroups(proposals: readonly Proposal[]): ProposalGroup[] {
  const groups: ProposalGroup[] = [];
  for (const proposal of proposals) {
    const last = groups.at(-1);
    const sameTier = last !== undefined && (last.tier === 'T3') === (proposal.tier === 'T3');
    if (last !== undefined && sameTier && last.label === proposal.kindLabel) last.items.push(proposal);
    else groups.push({ label: proposal.kindLabel, tier: proposal.tier, items: [proposal] });
  }
  return groups;
}

/**
 * Whether approving needs the 1.5 s hold on the detail screen. Fail closed: only a proposal
 * that is positively T3 with a non-hold executor is a tap; a T4 needs the hold whatever its
 * executor action is, and so does any tier the panel does not know.
 */
export function requiresHold(proposal: Proposal): boolean {
  return proposal.tier !== 'T3' || proposal.executor.kind === 'hold_apply';
}

/** Düzelt edits the outgoing text and approves in one step, so it exists only for tap (T3) items. */
export function canEdit(proposal: Proposal): boolean {
  return !requiresHold(proposal) && (proposal.kind === 'customer_reply' || proposal.kind === 'listing_title');
}

/**
 * A coupon above the discount cap cannot be approved ("Politika dışı"). The stricter of the
 * proposal's cap and the global policy applies; a value that is not a finite number counts
 * as outside the policy.
 */
export function outsidePolicy(proposal: Proposal, snapshot: Snapshot | null): boolean {
  if (proposal.kind !== 'coupon') return false;
  const caps = [proposal.policy.capPct, snapshot?.policy.discountCapPct ?? proposal.policy.capPct];
  if (!caps.every((cap) => Number.isFinite(cap))) return true;
  const discount = proposal.terms.discountPct;
  return !Number.isFinite(discount) || discount > Math.min(...caps);
}

/** Open requests: customer waiting first, then the oldest. */
export function openRequests(snapshot: Snapshot): TwinRequest[] {
  return snapshot.requests
    .filter((request) => request.status === 'open')
    .sort((a, b) => Number(b.customerWaiting) - Number(a.customerWaiting) || a.createdAt - b.createdAt);
}

export function requestsWithStatus(snapshot: Snapshot, status: 'accepted' | 'deferred'): TwinRequest[] {
  return snapshot.requests.filter((request) => request.status === status);
}

export interface Counts {
  approvals: number;
  requests: number;
  total: number;
  t3: number;
  t4: number;
  t4Topics: string[];
  customersWaiting: number;
  questions: number;
  designs: number;
  photos: number;
  requestCustomersWaiting: number;
}

export function counts(snapshot: Snapshot): Counts {
  const open = openRequests(snapshot);
  const t4 = snapshot.proposals.filter((proposal) => proposal.tier === 'T4');
  return {
    approvals: snapshot.proposals.length,
    requests: open.length,
    total: snapshot.proposals.length + open.length,
    t3: snapshot.proposals.length - t4.length,
    t4: t4.length,
    t4Topics: [...new Set(t4.map((proposal) => proposal.topic))],
    customersWaiting: snapshot.proposals.filter((proposal) => proposal.waiting === 'müşteri bekliyor').length,
    questions: open.filter((request) => request.type === 'question').length,
    designs: open.filter((request) => request.type === 'design').length,
    photos: open.filter((request) => request.type === 'photo').length,
    requestCustomersWaiting: open.filter((request) => request.customerWaiting).length,
  };
}

/** Age of the oldest approval in milliseconds, or null for an empty queue. */
export function oldestApprovalAge(snapshot: Snapshot, now: number): number | null {
  if (snapshot.proposals.length === 0) return null;
  return now - Math.min(...snapshot.proposals.map((proposal) => proposal.queuedAt));
}

export interface AgentCounts {
  working: number;
  waiting: number;
  idle: number;
  paused: number;
  error: number;
}

export function agentCounts(agents: readonly Agent[]): AgentCounts {
  const result: AgentCounts = { working: 0, waiting: 0, idle: 0, paused: 0, error: 0 };
  for (const agent of agents) {
    if (agent.state === 'working') result.working += 1;
    else if (agent.state === 'waiting') result.waiting += 1;
    else if (agent.state === 'idle') result.idle += 1;
    else if (agent.state === 'paused') result.paused += 1;
    else result.error += 1;
  }
  return result;
}

/**
 * The health words of the status lines (audit 25 Oct 2026, F5): "Her şey yolunda" only when no agent
 * fails or is paused and the executor runs; otherwise the problems, worst first.
 */
export interface SystemHealth {
  ok: boolean;
  text: string;
}

export function systemHealth(snapshot: Snapshot): SystemHealth {
  const team = agentCounts(snapshot.agents);
  const problems: string[] = [];
  if (team.error > 0) problems.push(`${team.error} ajan hata verdi`);
  if (!snapshot.executor.ready) problems.push('yürütücü durdu');
  if (team.paused > 0) problems.push(`${team.paused} ajan duraklatıldı`);
  if (problems.length === 0) return { ok: true, text: 'Her şey yolunda' };
  const text = problems.join(' · ');
  return { ok: false, text: text.charAt(0).toLocaleUpperCase('tr-TR') + text.slice(1) };
}

/** "2 ajan çalışıyor" headline for the Ekip summary. */
export function teamHeadline(c: AgentCounts): string {
  if (c.error > 0) return `${c.error} ajan hata verdi`;
  if (c.working === 0) return 'Şu an çalışan ajan yok';
  return `${c.working} ajan çalışıyor`;
}

/** "1 bekliyor · 1 boşta · 0 hata" */
export function teamSubline(c: AgentCounts): string {
  const parts = [`${c.waiting} bekliyor`, `${c.idle} boşta`];
  if (c.paused > 0) parts.push(`${c.paused} duraklatıldı`);
  parts.push(`${c.error} hata`);
  return parts.join(' · ');
}

export function agentById(snapshot: Snapshot, id: AgentId): Agent | undefined {
  return snapshot.agents.find((agent) => agent.id === id);
}

export const AGENT_NAMES: Record<AgentId, string> = {
  gm: 'Genel Müdür',
  temsilci: 'Temsilci',
  icerik: 'İçerik ve SEO',
  denetci: 'Denetçi',
};

export const AGENT_SHAPES: Record<AgentId, 'circle' | 'square' | 'diamond' | 'hexagon'> = {
  gm: 'circle',
  temsilci: 'square',
  icerik: 'diamond',
  denetci: 'hexagon',
};

/** Turkish possessive/accusative suffix after a name for aria-labels: "Temsilci'yi", "Denetçi'yi". */
export function accusative(name: string): string {
  const special: Record<string, string> = {
    'Genel Müdür': "Genel Müdür'ü",
    Temsilci: "Temsilci'yi",
    'İçerik ve SEO': "İçerik ve SEO'yu",
    Denetçi: "Denetçi'yi",
  };
  return special[name] ?? `${name}'i`;
}

/** Gönderilecekler (v0.1.54): approved e-mail replies not sent yet. */
export function outboxWaiting(snapshot: Snapshot): number {
  // v0.1.114: approved delivery messages wait here too until Gönderdim.
  const deliveries = (snapshot.deliveries ?? []).filter((item) => !item.sent).length;
  // v0.1.223: approved new Etsy listing drafts wait here until Yaptım.
  const listings = (snapshot.listingDrafts ?? []).filter((item) => !item.done).length;
  return snapshot.outbox.filter((item) => item.send.status !== 'sent').length + deliveries + listings;
}

/**
 * One inbox number (audit 25 Oct 2026, F14): approvals, open requests, messages to send by hand and open
 * Sekreter items, the same count on Bugün, the badges and the review button.
 */
export interface Inbox {
  approvals: number;
  requests: number;
  outbox: number;
  sekreter: number;
  total: number;
}

export function inbox(snapshot: Snapshot): Inbox {
  const c = counts(snapshot);
  const outbox = outboxWaiting(snapshot);
  const sekreter = snapshot.attention.openSekreterItems;
  return {
    approvals: c.approvals,
    requests: c.requests,
    outbox,
    sekreter,
    total: c.approvals + c.requests + outbox + sekreter,
  };
}

/** The inbox in words with the page of each part: "3 onay", "4 istek", "2 gönderilecek", "Sekreter'de 1 madde". */
export function inboxParts(box: Inbox): { key: keyof Omit<Inbox, 'total'>; text: string; to: string }[] {
  const parts: { key: keyof Omit<Inbox, 'total'>; text: string; to: string }[] = [];
  if (box.approvals > 0) parts.push({ key: 'approvals', text: `${box.approvals} onay`, to: '/onaylar' });
  if (box.requests > 0) parts.push({ key: 'requests', text: `${box.requests} istek`, to: '/istekler' });
  if (box.outbox > 0) parts.push({ key: 'outbox', text: `${box.outbox} gönderilecek`, to: '/gonderilecekler' });
  if (box.sekreter > 0) parts.push({ key: 'sekreter', text: `Sekreter'de ${box.sekreter} madde`, to: '/sekreter' });
  return parts;
}

/** Where "Sırayla incele" starts: the first approval, else the first page that has something waiting. */
export function reviewStart(snapshot: Snapshot): string | null {
  const first = orderedProposals(snapshot)[0];
  if (first) return `/onay/${first.id}`;
  return inboxParts(inbox(snapshot))[0]?.to ?? null;
}
